import React, { useState } from 'react';
import { ICON_DISPLAY } from '../card-editor/constants';

/*
 * The merchant's loyalty card, drawn the way it looks in Apple Wallet:
 * card colour, logo, optional banner with the stamps on it, reward line.
 * Shared by the customer pages (join, wallet) and the merchant scan screen.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Colour helpers: readable text on the merchant's card colour ── */
const hexToRgb = (hex) => {
  const h = (hex || '').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  if (Number.isNaN(n) || full.length !== 6) return [37, 99, 235];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const luminance = (hex) => {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrast = (a, b) => {
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
};

/** { bg, fg }: the card colour and a text colour that stays readable on it. */
export const cardTheme = (card) => {
  const bg = card?.primary_color || '#2563EB';
  const preferred = card?.secondary_color || '#FFFFFF';
  const fallback = luminance(bg) > 0.45 ? '#15171A' : '#FFFFFF';
  const fg = contrast(bg, preferred) >= 3 ? preferred : fallback;
  return { bg, fg };
};

/** Uploaded files are served by the API ("/api/files/…"). */
export const resolveAssetUrl = (url) => (url && url.startsWith('/') ? `${API_URL}${url}` : url);

/**
 * <img> that disappears instead of showing the browser's broken-image icon
 * when the file can't be loaded (deleted upload, storage down, bad link).
 * The `key` makes it try again when the address changes.
 */
export const SafeImage = ({ src, ...props }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return <img src={src} onError={() => setFailed(true)} alt="" {...props} />;
};

const stampSymbol = (icon) => {
  if (!icon) return { url: null, emoji: null };
  if (icon.startsWith('http')) return { url: icon, emoji: null };
  return { url: null, emoji: ICON_DISPLAY[icon] || (icon.length <= 4 ? icon : null) };
};

/* ── Stamps on the card colour (no banner) ── */
export const StampGrid = ({ total, filled, fg, icon, justAdded = false, size: forced }) => {
  const size = forced || (total <= 6 ? 46 : total <= 10 ? 40 : 32);
  const { url, emoji } = stampSymbol(icon);
  return (
    <div className="flex flex-wrap gap-2" style={{ maxWidth: size * 5 + 8 * 4 }} role="img" aria-label={`${filled} tampons sur ${total}`}>
      {Array.from({ length: total }, (_, i) => {
        const on = i < filled;
        const isNew = justAdded && i === filled - 1;
        return (
          <div
            key={i}
            className={`rounded-full flex items-center justify-center shrink-0 ${isNew ? 'scan-stamp-in' : ''}`}
            style={{
              width: size, height: size,
              background: on ? fg : 'transparent',
              border: `2px solid ${fg}`,
              opacity: on ? 1 : 0.35,
            }}
          >
            {on && url && <SafeImage key={url} src={url} alt="" className="w-3/4 h-3/4 object-contain" />}
            {on && emoji && <span style={{ fontSize: size * 0.5, lineHeight: 1 }}>{emoji}</span>}
          </div>
        );
      })}
    </div>
  );
};

/* ── Stamps on top of the banner, like the real pass strip ── */
const BannerStamps = ({ total, filled, icon }) => {
  const cols = Math.min(5, total);
  const rows = Math.ceil(total / cols);
  const size = total <= 6 ? 13 : total <= 10 ? 11 : 8.5; // % of banner width
  const { url, emoji } = stampSymbol(icon);
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5" role="img" aria-label={`${filled} tampons sur ${total}`}>
      {Array.from({ length: rows }, (_, row) => {
        const start = row * cols;
        const count = Math.min(cols, total - start);
        return (
          <div key={row} className="w-full flex justify-center" style={{ columnGap: '2.6%' }}>
            {Array.from({ length: count }, (_, col) => {
              const on = start + col < filled;
              return (
                <div
                  key={col}
                  className="rounded-full flex items-center justify-center shrink-0 shadow-sm"
                  style={{ width: `${size}%`, aspectRatio: '1 / 1', backgroundColor: on ? '#FFFFFF' : 'rgba(255,255,255,0.6)' }}
                >
                  {on && url && <SafeImage key={url} src={url} alt="" style={{ width: '70%', height: '70%', objectFit: 'contain' }} />}
                  {on && emoji && <span className="text-[18px] leading-none">{emoji}</span>}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

const pluralize = (n, word) => `${n} ${word}${n > 1 ? 's' : ''}`;

/**
 * Progress sentence under the card.
 *   stamps: "Encore 7 tampons avant : 1 boisson offerte"
 *   points: "Encore 36 points avant : Un brunch offert"
 */
export const progressLine = (card, balance) => {
  const isStamps = card.card_type === 'stamps';
  const total = isStamps ? (card.total_stamps || 10) : (card.reward_threshold || 10);
  const remaining = Math.max(0, total - balance);
  if (!card.reward_description) return null;
  if (remaining === 0) return { lead: 'Récompense : ', reward: card.reward_description };
  return {
    lead: `Encore ${pluralize(remaining, isStamps ? 'tampon' : 'point')} avant : `,
    reward: card.reward_description,
  };
};

/**
 * Props:
 *   card     — public card fields (name, colours, logo_url, strip_url, card_type…)
 *   balance  — current stamps/points (0 on the join page)
 *   title    — big line on the card (customer name, or the promise on join)
 *   subtitle — optional small line under the title
 *   justAdded — animate the last stamp (scan screen)
 *   hideProgress — no "Encore N … avant" line (join page shows the promise instead)
 */
const LoyaltyCard = ({ card, balance = 0, title, subtitle, justAdded = false, hideProgress = false, className = '', children }) => {
  const { bg, fg } = cardTheme(card);
  const isStamps = card.card_type === 'stamps';
  const total = isStamps ? (card.total_stamps || 10) : (card.reward_threshold || 10);
  const logo = resolveAssetUrl(card.logo_url);
  const bannerUrl = resolveAssetUrl(card.strip_url || card.background_url);
  // If the banner can't be loaded, fall back to the stamps on the card colour.
  const [failedBanner, setFailedBanner] = useState(null);
  const banner = bannerUrl && failedBanner !== bannerUrl ? bannerUrl : null;
  const line = hideProgress ? null : progressLine(card, balance);

  return (
    <section
      className={`rounded-[22px] overflow-hidden shadow-[0_14px_36px_-14px_rgba(21,23,26,0.5)] ${className}`}
      style={{ background: bg, color: fg }}
    >
      <header className="flex items-center gap-3 px-5 pt-5 pb-4 min-h-[64px]">
        <SafeImage key={logo || 'no-logo'} src={logo} alt="" className="h-10 w-10 rounded-xl object-contain shrink-0" style={{ background: 'rgba(255,255,255,0.12)' }} />
        <p className="text-[16px] font-semibold leading-tight truncate flex-1">{card.name}</p>
        {isStamps && (
          <p className="text-[20px] font-bold tabular-nums shrink-0" aria-hidden="true">
            {Math.min(balance, total)}/{total}
          </p>
        )}
      </header>

      {isStamps && banner && (
        <div className="relative w-full" style={{ aspectRatio: '1125 / 432' }}>
          <img src={banner} alt="" onError={() => setFailedBanner(banner)} className="absolute inset-0 w-full h-full object-cover" />
          <BannerStamps total={total} filled={Math.min(balance, total)} icon={card.stamp_icon} />
        </div>
      )}

      <div className="px-5 pt-4 pb-5">
        {title && <h1 className="text-[28px] leading-[1.1] font-bold break-words">{title}</h1>}
        {subtitle && <p className="text-[16px] opacity-85 mt-1">{subtitle}</p>}

        {isStamps && !banner && (
          <div className="mt-5">
            <StampGrid total={total} filled={Math.min(balance, total)} fg={fg} icon={card.stamp_icon} justAdded={justAdded} />
          </div>
        )}
        {!isStamps && (
          <div className="mt-5">
            <div className="flex items-baseline justify-between mb-2 tabular-nums">
              <span className="text-[22px] font-bold">{balance} point{balance > 1 ? 's' : ''}</span>
              <span className="text-[15px] opacity-80">sur {total}</span>
            </div>
            <div className="h-3 rounded-full overflow-hidden" style={{ background: `${fg}33` }}>
              <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${Math.min(100, (balance / total) * 100)}%`, background: fg }} />
            </div>
          </div>
        )}

        {line && (
          <p className="text-[15px] leading-snug mt-5 opacity-90">
            {line.lead}<span className="font-semibold">{line.reward}</span>
          </p>
        )}
        {children}
      </div>
    </section>
  );
};

export default LoyaltyCard;
